'use client';

import React, { useState } from 'react';
import {
    Bell,
    Receipt,
    Calendar,
    CheckCircle2,
    XCircle,
    Megaphone,
    Clock,
    MoreHorizontal,
    Trash2,
    Filter,
    Search,
    Info,
    ArrowRight
} from 'lucide-react';

interface Notification {
    id: string;
    type: 'NewBill' | 'DueDate' | 'Received' | 'Rejected' | 'Announcement';
    title: string;
    message: string;
    timestamp: string;
    isRead: boolean;
    relatedId?: string; // ID Tagihan atau Transaksi
}

const dummyNotifications: Notification[] = [
    {
        id: '1',
        type: 'Received',
        title: 'Pembayaran Diterima',
        message: 'Pembayaran SPP Februari 2025 sebesar Rp 1.500.000 telah berhasil diverifikasi. Kwitansi digital tersedia di menu Riwayat.',
        timestamp: '10 menit yang lalu',
        isRead: false,
        relatedId: 'PAY-9012'
    },
    {
        id: '2',
        type: 'DueDate',
        title: 'Pengingat Jatuh Tempo',
        message: 'Tagihan Uang Kegiatan Outbound akan jatuh tempo dalam 3 hari (15 Mar 2025). Segera lakukan pembayaran untuk menghindari denda.',
        timestamp: '2 jam yang lalu',
        isRead: false,
        relatedId: 'INV-002'
    },
    {
        id: '3',
        type: 'Announcement',
        title: 'Pengumuman Kenaikan Biaya Gedung',
        message: 'Diberitahukan kepada seluruh wali murid bahwa akan ada penyesuaian biaya sarana prasarana untuk tahun ajaran 2025/2026. Detail dapar dilihat pada lampiran profil.',
        timestamp: 'Kemarin',
        isRead: true
    },
    {
        id: '4',
        type: 'NewBill',
        title: 'Tagihan Baru Diterbitkan',
        message: 'Tagihan SPP bulan Maret 2025 telah diterbitkan. Anda sudah bisa melakukan pembayaran mulai hari ini.',
        timestamp: '2 hari yang lalu',
        isRead: true,
        relatedId: 'INV-004'
    },
    {
        id: '5',
        type: 'Rejected',
        title: 'Pembayaran Ditolak',
        message: 'Bukti transfer untuk Uang Seragam tidak valid atau nominal tidak sesuai. Mohon unggah kembali bukti yang benar.',
        timestamp: '3 hari yang lalu',
        isRead: true,
        relatedId: 'PAY-8799'
    }
];

export default function WalimuridNotifications() {
    const [notifications, setNotifications] = useState<Notification[]>(dummyNotifications);
    const [filter, setFilter] = useState<'All' | 'Unread'>('All');
    const [searchTerm, setSearchTerm] = useState('');

    const getIcon = (type: string) => {
        switch (type) {
            case 'NewBill': return <Receipt className="w-5 h-5 text-primary" />;
            case 'DueDate': return <Calendar className="w-5 h-5 text-warning" />;
            case 'Received': return <CheckCircle2 className="w-5 h-5 text-success" />;
            case 'Rejected': return <XCircle className="w-5 h-5 text-error" />;
            case 'Announcement': return <Megaphone className="w-5 h-5 text-info" />;
            default: return <Bell className="w-5 h-5" />;
        }
    };

    const getBgColor = (type: string) => {
        switch (type) {
            case 'NewBill': return 'bg-primary/5 border-primary/20';
            case 'DueDate': return 'bg-warning/5 border-warning/20';
            case 'Received': return 'bg-success/5 border-success/20';
            case 'Rejected': return 'bg-error/5 border-error/20';
            case 'Announcement': return 'bg-info/5 border-info/20';
            default: return 'bg-muted/5 border-muted/20';
        }
    };

    const markAllAsRead = () => {
        setNotifications(notifications.map(n => ({ ...n, isRead: true })));
    };

    const filteredNotifications = notifications.filter(n => {
        const matchesStatus = filter === 'All' || !n.isRead;
        const matchesSearch = n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            n.message.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesStatus && matchesSearch;
    });

    return (
        <div className="space-y-10 animate-in fade-in duration-700 pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 min-h-[80px]">
                <div>
                    <h1 className="text-3xl font-black text-foreground tracking-tight flex items-center gap-4">
                        <Bell className="w-8 h-8 text-primary" /> Pusat Notifikasi Finansial
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 font-medium italic">Update real-time mengenai tagihan, status pembayaran, dan pengumuman resmi.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                        <input
                            type="text"
                            placeholder="Cari notifikasi..."
                            className="bg-surface border border-border-main pl-10 pr-4 py-3 rounded-2xl text-xs font-bold w-full md:w-64 focus:border-primary outline-none transition-all shadow-xs"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={markAllAsRead}
                        className="flex items-center gap-2 px-5 py-3 bg-surface border border-border-main rounded-2xl text-xs font-bold text-muted-foreground hover:text-primary transition-all active:scale-95"
                    >
                        <CheckCircle2 className="w-4 h-4" /> Tandai Semua Dibaca
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left: Notifications List */}
                <div className="lg:col-span-8 space-y-6">
                    {/* Filter Tabs */}
                    <div className="flex items-center gap-4 p-2 bg-surface border border-border-main rounded-2xl w-fit">
                        <button
                            onClick={() => setFilter('All')}
                            className={`px-6 py-2 rounded-xl text-xs font-black transition-all ${filter === 'All' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-muted-foreground hover:bg-background'}`}
                        >
                            Semua ({notifications.length})
                        </button>
                        <button
                            onClick={() => setFilter('Unread')}
                            className={`px-6 py-2 rounded-xl text-xs font-black transition-all ${filter === 'Unread' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-muted-foreground hover:bg-background'}`}
                        >
                            Belum Dibaca ({notifications.filter(n => !n.isRead).length})
                        </button>
                    </div>

                    {/* Timeline */}
                    <div className="space-y-4">
                        {filteredNotifications.length > 0 ? filteredNotifications.map((notif) => (
                            <div
                                key={notif.id}
                                className={`relative group p-8 rounded-[32px] border-2 transition-all hover:shadow-xl hover:shadow-primary/5 cursor-pointer ${notif.isRead ? 'bg-surface border-border-main opacity-80' : 'bg-surface border-primary shadow-lg shadow-primary/10'}`}
                            >
                                <div className="flex gap-6">
                                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-xs group-hover:scale-110 transition-transform ${getBgColor(notif.type)}`}>
                                        {getIcon(notif.type)}
                                    </div>
                                    <div className="flex-1 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <h3 className="text-base font-black text-foreground tracking-tight">{notif.title}</h3>
                                                {!notif.isRead && <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>}
                                            </div>
                                            <span className="text-[10px] font-bold text-muted-foreground flex items-center gap-1.5 opacity-60">
                                                <Clock className="w-3 h-3" /> {notif.timestamp}
                                            </span>
                                        </div>
                                        <p className="text-sm font-medium text-muted-foreground leading-relaxed">
                                            {notif.message}
                                        </p>

                                        {notif.relatedId && (
                                            <button className="flex items-center gap-2 text-[10px] font-black text-primary uppercase tracking-widest pt-4 group/link animate-in slide-in-from-left-2">
                                                Lihat Rincian {notif.type === 'Received' || notif.type === 'Rejected' ? 'Transaksi' : 'Tagihan'}
                                                <ArrowRight className="w-3.5 h-3.5 group-hover/link:translate-x-1 transition-transform" />
                                            </button>
                                        )}
                                    </div>
                                    <button className="p-2 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-error h-fit">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>

                                {!notif.isRead && (
                                    <div className="absolute top-4 right-10">
                                        <p className="text-[9px] font-black text-primary uppercase tracking-[0.2em] italic">New Update</p>
                                    </div>
                                )}
                            </div>
                        )) : (
                            <div className="p-20 bg-surface border-2 border-dashed border-border-main rounded-[40px] flex flex-col items-center justify-center text-center space-y-4">
                                <div className="w-16 h-16 bg-muted/10 rounded-full flex items-center justify-center text-muted-foreground">
                                    <Bell className="w-8 h-8 opacity-20" />
                                </div>
                                <div className="space-y-1">
                                    <p className="text-lg font-black text-foreground">Tidak Ada Notifikasi</p>
                                    <p className="text-sm text-muted-foreground font-medium italic">Anda sudah membaca seluruh pengumuman hari ini.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right: Info Panels */}
                <div className="lg:col-span-4 space-y-8">
                    {/* Source of Truth Information */}
                    <div className="bg-surface border border-border-main rounded-[40px] p-10 shadow-xs space-y-8 italic">
                        <div className="space-y-4">
                            <h3 className="text-xl font-bold flex items-center gap-3">
                                <Info className="w-6 h-6 text-primary" /> Alur Informasi
                            </h3>
                            <p className="text-xs text-muted-foreground font-medium leading-relaxed">
                                Seluruh notifikasi berasal dari aktivitas resmi keuangan sekolah/yayasan yang terverifikasi.
                            </p>
                        </div>

                        <div className="space-y-6">
                            <div className="flex gap-4 p-4 bg-background rounded-2xl border border-border-main border-dashed">
                                <div className="w-8 h-8 rounded-xl bg-primary/5 flex items-center justify-center text-primary shrink-0">
                                    <Receipt className="w-4 h-4" />
                                </div>
                                <div className="space-y-0.5">
                                    <p className="text-[10px] font-black text-foreground leading-none">Bill Creation</p>
                                    <p className="text-[9px] text-muted-foreground font-medium">Berasal dari input iuran rutin di Dashboard Sekolah.</p>
                                </div>
                            </div>

                            <div className="flex gap-4 p-4 bg-background rounded-2xl border border-border-main border-dashed">
                                <div className="w-8 h-8 rounded-xl bg-success/5 flex items-center justify-center text-success shrink-0">
                                    <CheckCircle2 className="w-4 h-4" />
                                </div>
                                <div className="space-y-0.5">
                                    <p className="text-[10px] font-black text-foreground leading-none">Payment Callback</p>
                                    <p className="text-[9px] text-muted-foreground font-medium">Notifikasi instan dari VA/QRIS gateway setelah transaksi sukses.</p>
                                </div>
                            </div>

                            <div className="flex gap-4 p-4 bg-background rounded-2xl border border-border-main border-dashed">
                                <div className="w-8 h-8 rounded-xl bg-info/5 flex items-center justify-center text-info shrink-0">
                                    <Megaphone className="w-4 h-4" />
                                </div>
                                <div className="space-y-0.5">
                                    <p className="text-[10px] font-black text-foreground leading-none">Policy Updates</p>
                                    <p className="text-[9px] text-muted-foreground font-medium">Pengumuman global yang diterbitkan oleh Admin Yayasan.</p>
                                </div>
                            </div>
                        </div>

                        <div className="pt-8 border-t border-border-main flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
                            <p className="text-[10px] font-bold text-muted-foreground italic">Sistem tersinkronisasi 1:1 dengan data keuangan.</p>
                        </div>
                    </div>

                    {/* Filter Academy/Finance Reminder */}
                    <div className="p-8 bg-warning/5 border-2 border-dashed border-warning/20 rounded-[32px] space-y-4">
                        <h4 className="text-sm font-black text-warning uppercase tracking-widest flex items-center gap-2">
                            Fokus Keuangan
                        </h4>
                        <p className="text-xs text-warning/70 font-medium leading-relaxed italic">
                            Halaman ini hanya menampilkan notifikasi terkait anggaran, biaya, dan administrasi. Notifikasi akademik (nilai, kehadiran) dapat dilihat pada portal akademik terpisah.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
